import { Injectable } from '@angular/core';

export interface ImageDimensions {
    width: number;
    height: number;
}

export interface SquareCropArea {
    sourceX: number;
    sourceY: number;
    sourceSize: number;
}

@Injectable({
    providedIn: 'root',
})
export class ImageValidatorService {
    private allowedImageTypes: string[] = [
        'image/jpeg',
        'image/jpg',
        'image/png',
    ];

    isImage(fileType: string): boolean {
        return this.allowedImageTypes.includes(fileType);
    }

    async getDimensions(file: File): Promise<ImageDimensions | null> {
        try {
            const bitmap: ImageBitmap = await createImageBitmap(file);
            const dimensions: ImageDimensions = {
                width: bitmap.width,
                height: bitmap.height,
            };

            bitmap.close();

            return dimensions;
        } catch {
            return null;
        }
    }

    async hasExactDimensions(
        file: File,
        width: number,
        height: number
    ): Promise<boolean> {
        const dimensions: ImageDimensions | null =
            await this.getDimensions(file);

        return dimensions?.width === width && dimensions?.height === height;
    }

    async cropToSquare(
        file: File,
        size: number,
        area?: SquareCropArea
    ): Promise<File | null> {
        let bitmap: ImageBitmap | null = null;

        try {
            bitmap = await createImageBitmap(file);

            const cropArea: SquareCropArea = area ?? {
                sourceX: Math.max(0, (bitmap.width - bitmap.height) / 2),
                sourceY: Math.max(0, (bitmap.height - bitmap.width) / 2),
                sourceSize: Math.min(bitmap.width, bitmap.height),
            };

            const canvas: HTMLCanvasElement = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;

            const context: CanvasRenderingContext2D | null =
                canvas.getContext('2d');

            if (!context) {
                return null;
            }

            context.imageSmoothingQuality = 'high';
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, size, size);
            context.drawImage(
                bitmap,
                cropArea.sourceX,
                cropArea.sourceY,
                cropArea.sourceSize,
                cropArea.sourceSize,
                0,
                0,
                size,
                size
            );

            const type: string =
                file.type === 'image/png' ? 'image/png' : 'image/jpeg';

            const blob: Blob | null = await new Promise((resolve) =>
                canvas.toBlob(resolve, type, 0.92)
            );

            if (!blob) {
                return null;
            }

            return new File([blob], file.name, { type });
        } catch {
            return null;
        } finally {
            bitmap?.close();
        }
    }
}
